import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LuClock, LuStethoscope, LuTrendingUp } from "react-icons/lu";
import "../styles/AboutUs.css";
import "../styles/Home.css";

const MissionIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mission-header-icon" aria-hidden="true">
    <path d="M13.5 8a2.5 2.5 0 0 1 3.5 3.5l-5 5-5-5A2.5 2.5 0 0 1 10.5 8L12 9.5Z" />
    <path d="M3 12h3l2-3 2 6 2-3h3" />
  </svg>
);

const HOW_IT_WORKS = [
  {
    heading: "Understand & Track Your Symptoms",
    text: "Our proprietary symptom calculators provide you with a symptom score that allows you to stay on top of your goals and receive the treatment you need.",
  },
  {
    heading: "Meet Your Urology-Trained Provider",
    text: "Book same or next-day appointments with a urology-trained provider and message your provider through our app to always remain involved in your care.",
  },
  {
    heading: "Learn About Your Diagnosis",
    text: "Access up-to-date, peer-reviewed educational material specific to your diagnosis directly through our app and always stay informed about the care you receive.",
  },
  {
    heading: "Get Individualized Care",
    text: "Personalized care plans designed meticulously for each patient to improve your symptoms and help you gain control of your urologic health.",
  },
  {
    heading: "Reach Your Peak Urologic Health",
    text: "Youro makes it simple and enjoyable to stay on top of your urologic health with our unique approach to compassionate care that is delivered by urology-trained providers.",
  },
];

export default function AboutUs() {
  const navigate = useNavigate();

  return (
    <div className="about-page">
      {/* Top nav (same structure as your other pages) */}
        <header className="home-nav">
          <Link to="/" className="home-nav-left" aria-label="Youro home">
            <div className="home-nav-logo-circle">Y</div>
            <span className="home-nav-logo-text">Youro</span>
          </Link>
          <nav className="home-nav-center">
            <button
              className="home-nav-link home-nav-link-active"
              onClick={() => navigate("/about")}
            >
              About
            </button>
            <button
              className="home-nav-link"
              onClick={() => navigate("/conditions")}
            >
              Find Your Diagnosis
            </button>
            <button
              className="home-nav-link"
              onClick={() => navigate("/assessment")}
            >
              Symptom Assessment
            </button>
          </nav>
          <div className="home-nav-right">
            <button
              className="home-nav-signin"
              onClick={() => navigate("/login")}
            >
              Sign In
            </button>
            <button
              className="home-nav-get-started"
              onClick={() => navigate("/signup")}
            >
              Get Started
            </button>
          </div>
        </header>

      {/* Scroll container */}
      <main className="about-scroll">
        <div className="about-inner">
          <button className="about-back" onClick={() => navigate("/")}>
            ← <span>Back to Home</span>
          </button>

          <h1 className="about-title">About Youro</h1>
          <p className="about-subtitle">
            Founded by urologists, transforming urologic care with a digital-first approach
          </p>

          {/* Our Mission */}
          <section className="about-card about-card-mission">
            <div className="mission-header-bar">
              <MissionIcon />
              <span className="mission-header-label">Our Mission</span>
            </div>

            <div className="mission-grid">
              <div className="mission-col">
                <LuClock size={32} strokeWidth={1.5} className="mission-col-icon" />
                <h3 className="mission-col-title">Access</h3>
                <p className="mission-col-text">
                  Skip the long waits. Get same-day or next-day urology care and expert guidance when you need
                  it from the comfort of your home, with seamless coordination for in-person visits when needed.
                </p>
              </div>

              <div className="mission-col">
                <LuStethoscope size={32} strokeWidth={1.5} className="mission-col-icon" />
                <h3 className="mission-col-title">Expertise</h3>
                <p className="mission-col-text">
                  We bring together evidence-based medicine and modern technology, connecting you with
                  urology-trained specialists who understand the complexities and sensitivities of urologic health.
                </p>
              </div>

              <div className="mission-col">
                <LuTrendingUp size={32} strokeWidth={1.5} className="mission-col-icon" />
                <h3 className="mission-col-title">Your Journey</h3>
                <p className="mission-col-text">
                  Care works best when you're involved. Learn about your diagnosis and treatment options,
                  log habits that impact your health, and track your progress over time.
                </p>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="about-card">
            <h2 className="about-card-title">How It Works</h2>
            <p className="about-card-subtitle">
              Our comprehensive approach to urologic care in five simple steps
            </p>

            <ol className="how-list">
              {HOW_IT_WORKS.map((step, i) => (
                <li key={step.heading} className="how-item">
                  <div className="how-badge">{String(i + 1).padStart(2, "0")}</div>
                  <div className="how-content">
                    <h3 className="how-heading">{step.heading}</h3>
                    <p className="how-text">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* CTA */}
          <section className="about-cta">
            <h2 className="about-cta-title">Ready to experience a new standard of urologic care?</h2>
            <p className="about-cta-subtitle">
              A personalized, digital-first approach built by urology-trained
              providers — not general telehealth.
            </p>

            <div className="about-cta-actions">
              <button className="about-btn-primary" onClick={() => navigate("/signup")}>
                Start Your Care Journey <span className="about-arrow">→</span>
              </button>
              <button className="about-btn-secondary" onClick={() => navigate("/assessment")}>
                Take Symptom Assessment
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
